/**
 * @fileoverview useEncryptionStatus Hook - Tracks encryption readiness and master key availability
 *
 * This hook provides real-time status of the encryption system, including:
 * - Whether user has set up WebAuthn
 * - Whether master key is currently unlocked
 * - Encryption readiness status
 * - Device setup completion
 *
 * **Usage:**
 * ```tsx
 * const { isEncryptionReady, isUnlocked, needsSetup } = useEncryptionStatus();
 *
 * if (needsSetup) {
 *   // Show WebAuthn setup wizard
 * } else if (!isUnlocked) {
 *   // Show unlock dialog
 * } else {
 *   // Allow access to encrypted data
 * }
 * ```
 *
 * @module hooks/useEncryptionStatus
 */

import { useState, useEffect, useCallback } from 'react';
import { createLogger } from '@xanectech/lib';
import { clearSession } from '@xanectech/encryption';
import { hasWebAuthnCredentials } from '../services/WebAuthnService';
import { hasPrimaryDevice } from '../services/DeviceManagementService';
import {
  isUserUnlocked,
  markUserUnlocked,
  clearUnlockStatus,
} from '../services/UnlockStatusService';
import { getUserPreferences } from '../services/UserPreferencesService';
import { UNLOCK_DURATION_OPTIONS } from '../services/UnlockPreferencesService';
import { useAuth } from '../contexts/AuthContext';
import { clearPasswordCache } from '../lib/securePasswordManager';

const logger = createLogger('useEncryptionStatus');

const STATUS_EVENT = 'xanec:encryption-status';

const broadcastStatusChange = () => {
  try {
    window.dispatchEvent(new CustomEvent(STATUS_EVENT));
  } catch (error) {
    logger.warn('Failed to broadcast status change', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
};

export interface EncryptionStatus {
  isEncryptionReady: boolean; // Overall encryption readiness
  hasWebAuthnSetup: boolean; // User has registered WebAuthn credentials
  hasPrimaryDeviceSetup: boolean; // User has at least one primary device
  isUnlocked: boolean; // Master key is currently unlocked in session
  needsSetup: boolean; // User needs to complete initial setup
  needsUnlock: boolean; // User needs to unlock (has setup but not unlocked)
}

export function useEncryptionStatus() {
  const { user } = useAuth();
  const [status, setStatus] = useState<EncryptionStatus>({
    isEncryptionReady: false,
    hasWebAuthnSetup: false,
    hasPrimaryDeviceSetup: false,
    isUnlocked: false,
    needsSetup: true,
    needsUnlock: false,
  });
  const [isChecking, setIsChecking] = useState(false);
  const [checkedUserId, setCheckedUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Check encryption status
   */
  const checkStatus = useCallback(async () => {
    if (!user) {
      setStatus({
        isEncryptionReady: false,
        hasWebAuthnSetup: false,
        hasPrimaryDeviceSetup: false,
        isUnlocked: false,
        needsSetup: true,
        needsUnlock: false,
      });
      setCheckedUserId(null);
      setIsChecking(false);
      return;
    }

    setIsChecking(true);
    setError(null);

    try {
      // Check if user has WebAuthn credentials
      const hasCredentials = await hasWebAuthnCredentials(user.id);

      // Check if user has primary device
      const hasPrimary = await hasPrimaryDevice(user.id);

      // Check if master key is unlocked from IndexedDB
      const unlockedInDB = await isUserUnlocked(user.id);

      // Legacy users may have valid WebAuthn credentials without an
      // encryption_devices primary record. Credentials are sufficient to
      // consider setup complete and prompt unlock instead of setup.
      const hasCompletedSetup = hasCredentials || hasPrimary;
      const needsSetup = !hasCompletedSetup;
      const needsUnlock = hasCompletedSetup && !unlockedInDB;
      const isReady = hasCompletedSetup && unlockedInDB;

      const newStatus: EncryptionStatus = {
        isEncryptionReady: isReady,
        hasWebAuthnSetup: hasCredentials,
        hasPrimaryDeviceSetup: hasPrimary,
        isUnlocked: unlockedInDB,
        needsSetup,
        needsUnlock,
      };

      setStatus(newStatus);

      logger.info('Encryption status checked', {
        isEncryptionReady: newStatus.isEncryptionReady,
        hasWebAuthnSetup: newStatus.hasWebAuthnSetup,
        hasPrimaryDeviceSetup: newStatus.hasPrimaryDeviceSetup,
        isUnlocked: newStatus.isUnlocked,
        needsSetup: newStatus.needsSetup,
        needsUnlock: newStatus.needsUnlock,
      });
    } catch (error) {
      const errorMsg =
        error instanceof Error
          ? error.message
          : 'Failed to check encryption status';
      logger.error('Failed to check encryption status', {
        error: error instanceof Error ? error.message : String(error),
      });
      setError(errorMsg);
    } finally {
      // Mark this user as checked even if an error occurred so callers can stop
      // showing loading states and surface the error condition.
      setCheckedUserId(user.id);
      setIsChecking(false);
    }
  }, [user]);

  /**
   * Mark encryption as unlocked
   *
   * Called after successful unlock to update status and persist to IndexedDB.
   * Uses the user's preferred unlock duration from the database.
   */
  const markUnlocked = useCallback(async () => {
    if (!user) {
      logger.warn('Cannot mark unlocked: no user');
      return;
    }

    try {
      logger.info('Marking encryption as unlocked');

      // Fetch user's preferred duration from database
      let duration: number | null = 60 * 60 * 1000; // 1 hour default
      try {
        const preferences = await getUserPreferences(user.id);
        const expiryOption = UNLOCK_DURATION_OPTIONS.find(
          (o) => o.id === preferences.expiry,
        );
        if (expiryOption) {
          duration = expiryOption.durationMs;
        }
      } catch (error) {
        logger.warn(
          'Failed to fetch unlock duration preference, using default',
          { error: error instanceof Error ? error.message : String(error) },
        );
      }

      // Persist to IndexedDB with selected duration (null means never expire)
      await markUserUnlocked(user.id, duration);

      setStatus((prev) => ({
        ...prev,
        isUnlocked: true,
        needsUnlock: false,
        isEncryptionReady: prev.hasWebAuthnSetup || prev.hasPrimaryDeviceSetup,
      }));

      // Notify other hook instances to refresh their status
      broadcastStatusChange();

      logger.info('Successfully marked as unlocked', {
        userId: user.id,
        duration,
      });
    } catch (error) {
      logger.error('Error marking unlocked', {
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  }, [user]);

  /**
   * Mark encryption as locked
   *
   * Called on logout or session timeout to update status and clear IndexedDB
   */
  const markLocked = useCallback(async () => {
    if (!user) return;

    logger.info('Marking encryption as locked');

    // Clear in-memory keys and cached credentials to enforce lock
    clearSession();
    clearPasswordCache();

    // Clear from IndexedDB
    await clearUnlockStatus(user.id);

    setStatus((prev) => ({
      ...prev,
      isUnlocked: false,
      needsUnlock: prev.hasWebAuthnSetup || prev.hasPrimaryDeviceSetup,
      isEncryptionReady: false,
    }));

    // Notify other hook instances to refresh their status
    broadcastStatusChange();
  }, [user]);

  /**
   * Refresh status
   */
  const refresh = useCallback(async () => {
    await checkStatus();
  }, [checkStatus]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Check status on mount and when user changes
  useEffect(() => {
    void checkStatus();
  }, [checkStatus]);

  // Listen for global status changes (unlock/lock) from other hook instances
  useEffect(() => {
    const handleStatusChange = () => {
      void checkStatus();
    };

    window.addEventListener(STATUS_EVENT, handleStatusChange);
    return () => window.removeEventListener(STATUS_EVENT, handleStatusChange);
  }, [checkStatus]);

  return {
    ...status,
    user,
    isChecking: isChecking || (!!user && checkedUserId !== user.id),
    error,
    markUnlocked,
    markLocked,
    refresh,
    clearError,
  };
}
